import { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import axios from "axios";
import { toast } from "react-toastify";
import { getAll, getByID, update } from "../api/API";
import { useParams } from "react-router-dom";

import { getCurrentUser, isLoggedIn, updateUser } from "../utility/UserUtils";

import LoadingSpinner from "../components/Generic/LoadingSpinner";
import ProfileHeader from "../components/Profile/ProfileHeader";
import ProfileSummaryCard from "../components/Profile/ProfileSummaryCard";
import ExperienceModal from "../components/Profile/ExperienceModal";
import ProfileAboutSection from "../components/Profile/ProfileAboutSection";
import ProfileContactSection from "../components/Profile/ProfileContactSection";
import ProfileEducationSection from "../components/Profile/ProfileEducationSection";
import ProfileCohortsSection from "../components/Profile/ProfileCohortSection";
// import ProfileEventsSection from "../components/Profile/ProfileEventsSection";
import ProfileExperienceSection from "../components/Profile/ProfileExperienceSection";

import "../styles/MemberProfile.css";

const API_BASE = process.env.REACT_APP_API_URL;

const emptyAddress = {
  street: "",
  city: "",
  state: "",
  zip: "",
  country: "",
};

const emptyEducation = {
  school: "",
  degree: "",
  field: "",
  startYear: "",
  endYear: "",
};

const ProfilePage = () => {
  const [user, setUser] = useState(null);
  const [cohorts, setCohorts] = useState([]);

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const { id: routeUserId } = useParams();

  // FORM STATE
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    email: "",
    dob: "",
    avatar: "",
    address: emptyAddress,
    experiences: [],
    education: emptyEducation,

    industries: "",
    skills: [],
    linkedin: "",
    website: "",
    bio: "",
    socialLinks: { twitter: "", facebook: "", instagram: "" },
    cohort: null,
    canGiveReferral: true,
  });

  // EXPERIENCE MODAL
  const [showExpModal, setShowExpModal] = useState(false);
  const [expForm, setExpForm] = useState({
    companyName: "",
    jobTitle: "",
    industry: "",
    startDate: "",
    endDate: "",
    current: false,
    yearsInTech: "",
  });
  // LOAD USER
  useEffect(() => {
    const loadProfile = async () => {
      // console.group(" Profile Load Debug");

      // console.log("routeUserId:", routeUserId);
      // console.log("isLoggedIn:", isLoggedIn());

      if (!isLoggedIn()) {
        // console.warn(" User not logged in");
        // console.groupEnd();
        return;
      }

      const loggedInUser = getCurrentUser();
      // console.log("loggedInUser:", loggedInUser);

      if (!loggedInUser?.id) {
        // console.error(" loggedInUser.id missing");
        // console.groupEnd();
        return;
      }

      //  block invalid ids
      if (!routeUserId || routeUserId === loggedInUser.id) {
        const member = await getByID("members", loggedInUser.id);
        setUser(member);
        resetForm(member);
        return;
      }

      //  other user profile
      try {
        // console.log(" Fetching other user profile:", routeUserId);

        const member = await getByID("members", routeUserId);
        setUser(member);
        resetForm(member);
      } catch (err) {
        // console.error(" Failed to load member profile", err);
        toast.error("Profile not found");
      } finally {
        // console.groupEnd();
      }
    };

    loadProfile();
  }, [routeUserId]);

  const loggedInUser = getCurrentUser();

  const isOwnProfile =
    !!loggedInUser &&
    (!routeUserId ||
      routeUserId === "undefined" ||
      routeUserId === loggedInUser.id);

  // LOAD COHORTS
  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        const res = await getAll("cohorts", { limit: 100 });
        setCohorts(res.docs);
      } catch (err) {
        console.error("Failed to fetch cohorts", err);
      }
    };
    fetchCohorts();
  }, []);

  // RESET FORM WITH USER DATA
  const resetForm = (userData) => {
    setForm({
      firstName: userData.firstName || "",
      lastName: userData.lastName || "",
      email: userData.email || "",
      phone: userData.phone || "",
      avatar: userData.avatar || "",
      dob: userData.dob
        ? new Date(userData.dob).toISOString().split("T")[0]
        : "",
      address: userData.address || emptyAddress,
      experiences:
        userData.experiences?.length > 0 ? [userData.experiences[0]] : [],
      education: userData.education || emptyEducation,

      industries: userData.industries || "",
      skills: Array.isArray(userData.skills)
        ? userData.skills.map((s) => s.skill)
        : [],
      linkedin: userData.linkedin || "",
      website: userData.website || "",
      bio: userData.bio || "",
      socialLinks: userData.socialLinks || {
        twitter: "",
        facebook: "",
        instagram: "",
      },
      cohort: userData.cohort
        ? {
            id: userData.cohort.id,
            title: userData.cohort.title,
            range: userData.cohort.range,
          }
        : null,
      canGiveReferral: userData.canGiveReferral ?? true,
    });
  };

  // FULL VALIDATION
  const sectionValidators = {
    summary: (form) => {
      const errors = {};
      if (!form.firstName?.trim()) errors.firstName = "First name is required";
      if (!form.lastName?.trim()) errors.lastName = "Last name is required";
      return errors;
    },

    contact: (form) => {
      const errors = {};
      if (!form.phone?.trim()) errors.phone = "Phone is required";
      if (!form.address?.city?.trim()) errors.city = "City is required";
      if (!form.address?.zip?.trim()) errors.zip = "ZIP code is required";
      return errors;
    },

    about: (form) => {
      const errors = {};
      if (form.bio && form.bio.length > 500)
        errors.bio = "Bio cannot exceed 500 characters";
      return errors;
    },

    education: (education) => {
      const errors = {};
      if (!education.school?.trim()) errors.school = "Institution is required";
      if (!education.degree?.trim()) errors.degree = "Degree is required";
      return errors;
    },

    experience: (experience) => {
      const errors = {};
      if (!experience.companyName?.trim())
        errors.companyName = "Company name is required";
      if (!experience.jobTitle?.trim())
        errors.jobTitle = "Job title is required";
      if (!experience.startDate) errors.startDate = "Start date is required";
      return errors;
    },
  };

  const validateSection = (section, data) => {
    const validator = sectionValidators[section];
    if (!validator) return true;

    const validationErrors = validator(data);

    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      toast.error("Please fix the fields marked with an asterisk (*)");
      return false;
    }

    setErrors({});
    return true;
  };

  // AVATAR UPLOAD
  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (!f) return;
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const uploadProfilePhoto = async (fileToUpload) => {
    const formData = new FormData();

    formData.append(
      "_payload",
      JSON.stringify({
        alt: fileToUpload.name,
        prefix: "profile-photos",
      }),
    );
    formData.append("file", fileToUpload);

    const token = getCurrentUser()?.token;

    const res = await axios.post(`${API_BASE}/profile-photos`, formData, {
      withCredentials: true,
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    });

    return res.data;
  };

  const buildFullPayload = (overrides = {}) => ({
    firstName: form.firstName,
    lastName: form.lastName,
    email: form.email,
    phone: form.phone,
    dob: form.dob || null,

    avatar: user?.avatar?.id || user?.avatar || null,

    address: form.address,
    education: form.education,
    experiences: form.experiences,

    industries: form.industries,
    skills: form.skills.map((s) => ({ skill: s })),
    linkedin: form.linkedin,
    website: form.website,
    bio: form.bio,

    cohort:
      typeof form.cohort === "string" ? form.cohort : form.cohort?.id || null,

    role: user.role?.id || user.role,
    canGiveReferral: form.canGiveReferral,

    ...overrides, // section-specific updates
  });

  // SAVE SUMMARY (FIXED)
  const handleSave = async () => {
    if (!validateSection("summary", form)) return;
    if (!user) return;

    setLoading(true);

    try {
      let avatarId = user.avatar?.id || user.avatar || null;

      // upload avatar if changed
      if (file) {
        const uploadRes = await uploadProfilePhoto(file);
        avatarId = uploadRes.doc.id;
      }

      // const formData = new FormData();

      const payload = buildFullPayload({
        avatar: avatarId,
      });
      const res = await update(`members/${user.id}`, payload);

      const mergedUser = {
        ...user, // existing full user
        ...res.doc, // updated fields from backend
      };
      updateUser(mergedUser);
      setUser(mergedUser);
      resetForm(mergedUser);

      toast.success("Profile updated successfully!");
      setIsEditing(false);
    } catch (err) {
      console.error(err);
      toast.error("Failed to update profile");
    } finally {
      setLoading(false);
    }
  };

  // CANCEL SUMMARY
  const handleCancel = () => {
    resetForm(user);
    setFile(null);
    setPreview(null);
    setIsEditing(false);
    setErrors({});
  };

  // SECTION SAVE
  const updateProfileSection = async (updatedFields) => {
    if (!user) return;

    setLoading(true);
    try {
      const res = await update(`members/${user.id}`, updatedFields);

      const mergedUser = {
        ...user,
        ...res.doc,
      };

      updateUser(mergedUser);
      setUser(mergedUser);
      resetForm(mergedUser);

      toast.success("Profile updated!");
    } catch (err) {
      console.error(err);
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  // EXPERIENCE MODAL
  const openExperienceModal = () => {
    const exp = form.experiences[0];

    if (exp) {
      setExpForm({
        companyName: exp.companyName || "",
        jobTitle: exp.jobTitle || "",
        industry: exp.industry || "",
        startDate: exp.startDate ? exp.startDate.split("T")[0] : "",
        endDate: exp.endDate ? exp.endDate.split("T")[0] : "",
        current: exp.currentlyWorking || false,
        yearsInTech: exp.years ? String(exp.years) : "",
      });
    } else {
      setExpForm({
        companyName: "",
        jobTitle: "",
        industry: "",
        startDate: "",
        endDate: "",
        current: false,
        yearsInTech: "",
      });
    }

    setShowExpModal(true);
  };

  const handleExperienceSave = async () => {
    if (!validateSection("experience", expForm)) return;
    const payload = {
      companyName: expForm.companyName,
      jobTitle: expForm.jobTitle,
      industry: expForm.industry,
      startDate: expForm.startDate || null,
      endDate: expForm.current ? null : expForm.endDate || null,
      currentlyWorking: expForm.current,
      years: Math.max(0, Number(expForm.yearsInTech) || 0),
    };

    // Update UI state
    setForm((prev) => ({
      ...prev,
      experiences: [payload],
      canGiveReferral: expForm.canGiveReferral ?? prev.canGiveReferral,
    }));

    await updateProfileSection({
      experiences: [payload],
      canGiveReferral: expForm.canGiveReferral ?? true,
    });

    setShowExpModal(false);
  };

  const removeExperience = () => {
    setForm((prev) => ({
      ...prev,
      experiences: [],
    }));
  };

  // EXPERIENCE SUMMARY
  const primaryExperience = form.experiences[0] || null;
  const hasExperience =
    form.experiences.length > 0 &&
    (form.experiences[0].jobTitle ||
      form.experiences[0].companyName ||
      form.experiences[0].industry);

  // AVATAR URL
  const getAvatarUrl = () => {
    if (preview) return preview;
    if (user?.avatar?.url) return user.avatar.url;
    if (typeof user?.avatar === "string")
      return `${API_BASE}/media/${user.avatar}`;
    return "/assets/images/default-avatar.png";
  };

  if (!isLoggedIn()) {
    return (
      <div className="container text-center mt-5">
        <h2 className="text-secondary mb-3">Login Required</h2>
        <p className="text-secondary">
          Please login to see this member&apos;s full profile.
        </p>
        <button
          className="btn btn-primary rounded-pill mt-3 px-4"
          onClick={() => (window.location.href = "/login")}
        >
          Login
        </button>
      </div>
    );
  }
  return (
    <>
      {loading && (
        <div className="loading-overlay">
          <LoadingSpinner text="Updating Profile..." />
        </div>
      )}

      {showExpModal && (
        <ExperienceModal
          show={showExpModal}
          onHide={() => setShowExpModal(false)}
          expForm={expForm}
          setExpForm={setExpForm}
          onSave={handleExperienceSave}
          updateProfileSection={updateProfileSection}
          loading={loading}
        />
      )}

      <div
        className={`profile-wrapper bg-primary ${loading ? "page-blur" : ""}`}
      >
        <ProfileHeader avatarUrl={getAvatarUrl()} />

        <ProfileSummaryCard
          avatarUrl={getAvatarUrl()}
          onFileChange={handleFileChange}
          form={form}
          setForm={setForm}
          errors={errors}
          isEditing={isEditing && isOwnProfile}
          setIsEditing={isOwnProfile ? setIsEditing : () => {}}
          primaryExperience={primaryExperience}
          loading={loading}
          onSave={isOwnProfile ? handleSave : undefined}
          onCancel={isOwnProfile ? handleCancel : undefined}
          validateSection={validateSection}
          readOnly={!isOwnProfile}
        />

        <Container className="profile-content">
          <Row className="profile-grid">
            <Col md={6} className="mb-4">
              <ProfileAboutSection
                form={form}
                setForm={setForm}
                errors={errors}
                updateProfileSection={updateProfileSection}
                validateSection={validateSection}
                readOnly={!isOwnProfile}
              />
            </Col>
            {isOwnProfile && (
              <Col md={6} className="mb-4">
                <ProfileContactSection
                  form={form}
                  setForm={setForm}
                  updateProfileSection={updateProfileSection}
                  validateSection={validateSection}
                />
              </Col>
            )}

            <Col md={6} className="mb-4">
              <ProfileExperienceSection
                experiences={form.experiences}
                hasExperience={hasExperience}
                onAdd={openExperienceModal}
                onEdit={openExperienceModal}
                onRemove={removeExperience}
                updateProfileSection={updateProfileSection}
                validateSection={validateSection}
                readOnly={!isOwnProfile}
              />
            </Col>

            <Col md={6} className="mb-4">
              <ProfileEducationSection
                form={form}
                setForm={setForm}
                updateProfileSection={updateProfileSection}
                validateSection={validateSection}
                readOnly={!isOwnProfile}
              />
            </Col>

            <Col md={6} className="mb-4">
              <ProfileCohortsSection
                cohort={form.cohort}
                cohorts={cohorts}
                updateProfileSection={updateProfileSection}
                readOnly={!isOwnProfile}
                validateSection={validateSection}
              />
            </Col>

            {/* <Col md={6} className="mb-4">
              <ProfileEventsSection />
            </Col> */}
          </Row>
        </Container>
      </div>
    </>
  );
};

export default ProfilePage;
